import type { ChipProps } from '@jotpsych/ui';
import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { WaitingRoomEntry, WaitingRoomQueueState } from './types';

export const WAITING_ROOM_ACTIVE_STATES: readonly WaitingRoomQueueState[] = [
  'CHECKED_IN',
  'DEVICE_CHECK',
  'IN_QUEUE',
  'ADMITTED',
  'IN_CALL',
];

export function isAdmittableEntry(entry: WaitingRoomEntry): boolean {
  return entry.current_state === 'IN_QUEUE';
}

export function isWaitingRoomListEntry(entry: WaitingRoomEntry): boolean {
  return (
    entry.current_state === 'CHECKED_IN' ||
    entry.current_state === 'DEVICE_CHECK' ||
    entry.current_state === 'IN_QUEUE'
  );
}

export function isInCallWaitingRoomEntry(entry: WaitingRoomEntry): boolean {
  return entry.current_state === 'ADMITTED' || entry.current_state === 'IN_CALL';
}

export function getWaitingRoomEntrySublabel(entry: WaitingRoomEntry): string | null {
  switch (entry.current_state) {
    case 'CHECKED_IN':
      return 'Check-in in progress';
    case 'DEVICE_CHECK':
      return 'Setting up devices';
    default:
      return null;
  }
}

const WAITING_ROOM_STATE_LABELS: Record<WaitingRoomQueueState, string> = {
  CHECKED_IN: 'Checked in',
  DEVICE_CHECK: 'Device check',
  IN_QUEUE: 'In queue',
  ADMITTED: 'Admitted',
  IN_CALL: 'In call',
  COMPLETED: 'Completed',
  REMOVED: 'Removed',
  ABANDONED: 'Left waiting room',
};

const WAITING_ROOM_STATE_COLORS: Record<WaitingRoomQueueState, ChipProps['color']> = {
  CHECKED_IN: 'info',
  DEVICE_CHECK: 'warning',
  IN_QUEUE: 'primary',
  ADMITTED: 'success',
  IN_CALL: 'success',
  COMPLETED: 'default',
  REMOVED: 'error',
  ABANDONED: 'default',
};

export function getWaitingRoomStateLabel(state: WaitingRoomQueueState): string {
  return WAITING_ROOM_STATE_LABELS[state] ?? state;
}

export function getWaitingRoomStateColor(state: WaitingRoomQueueState): ChipProps['color'] {
  return WAITING_ROOM_STATE_COLORS[state] ?? 'default';
}

export function getWaitingRoomWaitColor(waitSeconds?: number | null): ChipProps['color'] {
  if (waitSeconds == null) {
    return 'default';
  }
  if (waitSeconds < 5 * 60) {
    return 'success';
  }
  if (waitSeconds <= 10 * 60) {
    return 'warning';
  }
  return 'error';
}

export function formatWaitingRoomWait(waitSeconds?: number | null): string {
  if (waitSeconds == null) {
    return 'Waiting';
  }

  if (waitSeconds < 60) {
    return 'Under 1 min';
  }

  const minutes = Math.floor(waitSeconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}

export function getWaitingRoomInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  return parts
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

export function getWaitingRoomDarkChipSx(color: ChipProps['color'] = 'default'): SxProps<Theme> {
  return theme => {
    if (!color || color === 'default') {
      return {
        bgcolor: alpha(theme.palette.common.white, 0.14),
        color: theme.palette.grey[100],
      };
    }

    const paletteColor = theme.palette[color];
    if (!paletteColor || typeof paletteColor !== 'object' || !('main' in paletteColor)) {
      return {
        bgcolor: alpha(theme.palette.common.white, 0.14),
        color: theme.palette.grey[100],
      };
    }

    return {
      bgcolor: alpha(paletteColor.main, 0.22),
      color: paletteColor.light ?? theme.palette.common.white,
      fontWeight: 700,
    };
  };
}

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const getErrorPayload = (error: unknown): Record<string, unknown> | undefined => {
  const errorRecord = asRecord(error);
  const data = asRecord(errorRecord?.data);
  const nestedData = asRecord(data?.data);

  return nestedData ?? data ?? errorRecord;
};

export function getWaitingRoomActionErrorCode(error: unknown): string | undefined {
  const payload = getErrorPayload(error);
  const errorCode = payload?.error_code;

  return typeof errorCode === 'string' ? errorCode : undefined;
}

const getWaitingRoomActionErrorMessage = (error: unknown): string | undefined => {
  const payload = getErrorPayload(error);
  const message = payload?.message;

  return typeof message === 'string' ? message : undefined;
};

const getWaitingRoomActionStatus = (error: unknown): number | undefined => {
  const errorRecord = asRecord(error);
  const status = errorRecord?.status;

  return typeof status === 'number' ? status : undefined;
};

const isNetworkError = (error: unknown) => {
  const status = getWaitingRoomActionStatus(error);

  return status === 0 || status === undefined;
};

interface WaitingRoomSubjectLanguageCopy {
  singular: string;
}

export function getWaitingRoomAdmitErrorMessage(
  error: unknown,
  subjectLanguage: WaitingRoomSubjectLanguageCopy,
): string {
  const errorCode = getWaitingRoomActionErrorCode(error);

  switch (errorCode) {
    case 'queue_entry_not_admittable':
    case 'invalid_state':
      return `This ${subjectLanguage.singular} is not ready to join yet. The queue has been refreshed.`;
    case 'encounter_room_not_created':
      return `Join the call first, then add this ${subjectLanguage.singular} from the people panel.`;
    case 'queue_entry_not_found':
      return `This ${subjectLanguage.singular} is no longer in the waiting room. The queue has been refreshed.`;
    case 'encounter_not_found':
    case 'encounter_not_recordable':
      return 'This encounter can no longer start a waiting-room call.';
    case 'queue_entry_not_owned':
    case 'encounter_not_owned':
      return `You do not have permission to add this ${subjectLanguage.singular} to the call.`;
    case 'daily_token_mint_failed':
      return 'Daily could not create a join token. Please try again.';
    default:
      if (isNetworkError(error)) {
        return `Network issue while adding this ${subjectLanguage.singular}. Please try again.`;
      }
      return (
        getWaitingRoomActionErrorMessage(error) ??
        `Could not add this ${subjectLanguage.singular} to the call.`
      );
  }
}

export function getWaitingRoomRemoveErrorMessage(
  error: unknown,
  subjectLanguage: WaitingRoomSubjectLanguageCopy,
): string {
  const errorCode = getWaitingRoomActionErrorCode(error);

  switch (errorCode) {
    case 'queue_entry_not_found':
      return 'This waiting-room entry is already gone. The queue has been refreshed.';
    case 'queue_entry_not_owned':
      return `You do not have permission to remove this ${subjectLanguage.singular} from the waiting room.`;
    case 'invalid_state':
      return `This ${subjectLanguage.singular} state changed before removal. The queue has been refreshed.`;
    default:
      if (isNetworkError(error)) {
        return `Network issue while removing this ${subjectLanguage.singular}. Please try again.`;
      }
      return (
        getWaitingRoomActionErrorMessage(error) ??
        `Could not remove this ${subjectLanguage.singular} from the waiting room.`
      );
  }
}
